import { Injectable } from '@angular/core';
import { Observable, first, tap } from 'rxjs';
import { ApiService, LoadingService, SnackbarService } from '../../services';
import { PayloadOrderOutput, Subscription } from '@core/models/subscription.model';
import { SubscriptionLoaderKey } from '@core/enums/subscription.enum';
import { ServiceList } from '@core/models';

const ENDPOINT = 'v1/subscriptions';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionApiService implements ServiceList<Subscription[]> {
  constructor(
    private api: ApiService,
    private loader: LoadingService,
    private snack: SnackbarService,
  ) {}

  list(): Observable<Subscription[]> {
    return this.api
      .get<Subscription[]>(`${ENDPOINT}`)
      .pipe(first(), this.loader.set(SubscriptionLoaderKey.GET_SUBSCRIPTION), this.snack.operator());
  }

  create(payload: Partial<PayloadOrderOutput>): Observable<Subscription> {
    return this.api.post<Subscription, Partial<PayloadOrderOutput>>(ENDPOINT, payload).pipe(
      first(),
      this.loader.set(SubscriptionLoaderKey.CREATE_SUBSCRIPTION),
      tap({
        complete: () => this.snack.success('Subscription created'),
        error: (err) => {
          if (err.error.message === 'Entity with the provided data already exists') {
            this.snack.error('Subscription description already exists');
          } else if (err.error.message === 'A shipment subscription with identical conditions already exists.') {
            this.snack.error('A subscription with the same settings already exists');
          } else {
            this.snack.error('Error creating subscription');
          }
        },
      }),
    );
  }

  toggle(id: string, enabled: boolean): Observable<Subscription> {
    return this.api.patch<Subscription, { enabled: boolean }>(`${ENDPOINT}/toggle/${id}`, { enabled }).pipe(
      first(),
      this.loader.set(SubscriptionLoaderKey.TOGGLE_SUBSCRIPTION),
      tap({
        complete: () => {
          enabled ? this.snack.success('Subscription enabled') : this.snack.success('Subscription disabled');
        },
        error: () => this.snack.error('Error updating subscription'),
      }),
    );
  }

  update(id: string, payload: Partial<PayloadOrderOutput>): Observable<Subscription> {
    return this.api.put<Subscription, Partial<PayloadOrderOutput>>(ENDPOINT, id, payload).pipe(
      first(),
      this.loader.set(SubscriptionLoaderKey.UPDATE_SUBSCRIPTION),
      tap({
        complete: () => this.snack.success('Subscription updated'),
        error: (err) => {
          if (err.error.message === 'Entity with the provided data already exists') {
            this.snack.error('Subscription description already exists');
          } else if (err.error.message === 'A shipment subscription with identical conditions already exists.') {
            this.snack.error('A subscription with the same settings already exists');
          } else {
            this.snack.error('Error updating subscription');
          }
        },
      }),
    );
  }

  delete(id: string): Observable<{ id: string }> {
    return this.api.delete<{ id: string }>(ENDPOINT, id).pipe(
      first(),
      this.loader.set(SubscriptionLoaderKey.DELETE_SUBSCRIPTION),
      tap({
        complete: () => this.snack.success('Subscription deleted'),
        error: () => this.snack.error('Error deleting subscription'),
      }),
    );
  }
}
